import { Route } from 'react-router-dom';
import type { ReactElement, ReactNode } from 'react';
import type { BrandConfig } from '@/brand/types';
import type { TranslateNav } from './buildMenu';
import { ScreenTabsProvider } from '@/shared/ui/ScreenTabs';
import type { ScreenTabsValue } from '@/shared/lib/screenTabsContext';
import type { ScreenId } from './types';
import { getScreen } from './screens';

// Роуты защищённой зоны собираются из реестра по списку экранов бренда.
// Порядок в массиве не влияет на матчинг — react-router ранжирует роуты по
// специфичности сам, поэтому список читается как «что включено», а не «в
// каком порядке проверять».
//
// Экраны, объединённые брендом в группу табов, дополнительно оборачиваются в
// ScreenTabsProvider: сам таб-бар рисует PageHeader на месте заголовка.
export function buildRoutes(config: BrandConfig, t: TranslateNav): ReactElement[] {
  const tabsByScreen = buildTabsIndex(config, t);

  return config.screens.map((id) => {
    const { path, element: Screen } = getScreen(id);
    const tabs = tabsByScreen.get(id);

    return (
      <Route key={id} path={path} element={withTabs(<Screen />, tabs)} />
    );
  });
}

// Куда ведёт корень кабинета. Отдельного поля в конфиге для этого нет: стартовым
// считается первый живой пункт меню бренда — у serious это дашборд, у wapclick
// (где дашборда нет) по-прежнему группа статистики, то есть /stat.
export function homePath(config: BrandConfig): string {
  const enabled = new Set(config.screens);

  for (const entry of config.nav) {
    const id = entry.kind === 'link' ? entry.screen : entry.primary;
    if (enabled.has(id)) return getScreen(id).path;
  }

  // Недостижимо при валидном конфиге (меню без единого живого пункта — это
  // кабинет без экранов), но корневой редирект — не место для исключения.
  return '/stat';
}

const withTabs = (screen: ReactNode, value?: ScreenTabsValue) =>
  value ? <ScreenTabsProvider value={value}>{screen}</ScreenTabsProvider> : screen;

// Для каждого экрана группы — общий заголовок раздела и полный список табов.
// Экраны, выключенные у бренда, из группы отпадают; группа, в которой остался
// один экран, табов не получает (единственный таб бессмыслен).
function buildTabsIndex(config: BrandConfig, t: TranslateNav): Map<ScreenId, ScreenTabsValue> {
  const index = new Map<ScreenId, ScreenTabsValue>();
  const enabled = new Set(config.screens);

  (config.tabGroups ?? []).forEach((group) => {
    const live = group.screens.filter((id) => enabled.has(id));
    if (live.length < 2) return;

    const tabs = live.map((id) => {
      const screen = getScreen(id);

      return {
        id,
        path: screen.path,
        label: t(screen.titleKey),
      };
    });

    // Без явного заголовка группы берём заголовок её первого живого экрана —
    // лучше, чем пустая шапка.
    const title = group.titleKey ? t(group.titleKey) : tabs[0].label;

    live.forEach((id) => index.set(id, { tabs, title }));
  });

  // Группы меню с compactTabs: ниже lg их подпункты — табы-сегменты на странице.
  // Заголовок группы не нужен (страница остаётся со своим), но поле обязательно —
  // кладём подпись группы, PageHeader при compact её не читает.
  config.nav.forEach((entry) => {
    if (entry.kind !== 'group' || !entry.compactTabs) return;

    const live = entry.items.filter((item) => enabled.has(item.screen));
    if (live.length < 2) return;

    const tabs = live.map((item) => {
      const screen = getScreen(item.screen);

      return {
        id: item.screen,
        path: screen.path,
        label: t(item.labelKey ?? screen.titleKey),
      };
    });

    live.forEach((item) => index.set(item.screen, { tabs, title: t(entry.labelKey), compact: true }));
  });

  return index;
}
